import { LatestGrid } from 'app/blocks/latest-grid'
import { ContentCardProps } from 'app/components/content/ContentCard'
import { ContentCardBookmarkable } from 'app/components/content/ContentCard/ContentCardBookmarkable'
import Section from 'app/components/structure/Section'
import React, { useCallback, useMemo } from 'react'
import {
  useWatchLaterContext,
  WatchLaterProvider,
} from 'app/providers/WatchLaterProvider'
import {
  contentListQueryToContentList,
  mapToContentCard,
} from 'app/queries/content/all-content-list'
import { createSearchQuery } from 'app/queries/search/search-content'
import usePagination from 'app/hooks/use-pagination'
import { Button } from '../primitive/Button'
import { Grid } from '../primitive/Grids'

const gridOptions = {
  $gtXs: { numColumns: 2 },
  $gtSm: { numColumns: 3 },
  $gtMd: { columnGap: 16, rowGap: 24 },
}

const query = createSearchQuery()

export const LatestGridBlock: React.FC<{ block: LatestGrid }> = ({ block }) => {
  const { content } = block || {}

  const initialItems = useMemo(
    () => (content || []).map((item: any) => mapToContentCard(item, {})),
    [content],
  )

  const limit = initialItems.length || 9

  const initialIds = useMemo(
    () => initialItems.map((item) => item.id).filter(Boolean),
    [initialItems],
  )

  const { data, hasMore, fetchMore, isFetchingNextPage } = usePagination(
    query,
    { isLatest: true, idsToRemove: initialIds, sort: '-publishedAt' },
    {
      queryKey: 'latest-grid',
      limit,
      formatFn: contentListQueryToContentList,
      enabled: initialItems.length > 0,
    },
  )

  const extraItems = data?.docs || []

  const items = useMemo(
    () => [...initialItems, ...extraItems],
    [initialItems, extraItems],
  )

  const contentIds = useMemo(
    () => items.map((item) => item.id).filter(Boolean),
    [items],
  )

  if (!items.length) {
    return null
  }

  return (
    <WatchLaterProvider contentIds={contentIds}>
      <LatestGridBlockInner
        title={block.title}
        items={items}
        hasMore={hasMore}
        fetchMore={fetchMore}
        isFetchingNextPage={isFetchingNextPage}
      />
    </WatchLaterProvider>
  )
}

const LatestGridBlockInner: React.FC<{
  title: string
  items: ContentCardProps[]
  hasMore: boolean | undefined
  fetchMore: () => void
  isFetchingNextPage: boolean
}> = ({ title, items, hasMore, fetchMore, isFetchingNextPage }) => {
  const { isInWatchLater, toggleWatchLater } = useWatchLaterContext()

  const renderItem = useCallback(
    (item: ContentCardProps) => (
      <ContentCardBookmarkable
        {...item}
        image={item.image2x}
        isInWatchLater={isInWatchLater(item.id)}
        onToggleWatchLater={toggleWatchLater}
        sizes='(min-width: 800px) 450px, (min-width: 660px) 50vw, 100vw'
      />
    ),
    [isInWatchLater, toggleWatchLater],
  )

  const keyExtractor = useCallback((item: ContentCardProps) => item.id, [])

  return (
    <Section paddedX gap="$6" title={title || 'Latest videos'}>
      <Grid
        items={items}
        renderItem={renderItem}
        options={gridOptions}
        keyExtractor={keyExtractor}
      />

      {hasMore && (
        <Button
          onPress={fetchMore}
          mx="auto"
          loading={isFetchingNextPage}
          uppercase
        >
          Load More
        </Button>
      )}
    </Section>
  )
}
